import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./HomePage.css";
import { IMAGES } from "../utils/GetImages";
import { getTranslation } from "../utils/translation";

export default function HomePage() {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const MainBackground = IMAGES["MainBackground.png"];
  const AboutWSOMImg = IMAGES["HomePage-AboutWSOM.png"];
  const LatestNewsImg = IMAGES["HomePage-LatestNews.png"];
  const BecomeAPlayerImg = IMAGES["HomePage-BecomeAPlayer.png"];
  const HostVenueImg = IMAGES["HomePage-HostVenue.png"];
  const ShopImg = IMAGES["HomePage-shop.png"];
  const PhotoPagePicture1 = IMAGES["PhotoPage-Picture1.png"];
  const PhotoPagePicture2 = IMAGES["PhotoPage-Picture2.png"];
  const PhotoPagePicture3 = IMAGES["PhotoPage-Picture3.png"];
  const AboutUsImg = IMAGES["HomePage-AboutUs.png"];
  const EventFallbackImg = IMAGES["HomePage-EventFallback.png"];

  const onContactClick = () => {
    navigate("/contact?from=HOME");
  };

  // 滾動到指定部分的函數
  const scrollToSection = (sectionId) => {
    console.log("Attempting to scroll to section:", sectionId);

    // 先嘗試直接使用 CSS scroll-behavior
    const element = document.querySelector(`#${sectionId}`);
    if (element) {
      console.log("Element found, scrolling...");
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    } else {
      console.error("Element not found with ID:", sectionId);
      // 列出所有存在的 section IDs 進行調試
      const allSections = document.querySelectorAll("section[id]");
      console.log(
        "Available sections:",
        Array.from(allSections).map((s) => s.id)
      );
    }
  };

  // 獲取事件數據
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // 獲取今天的日期作為startDate
        const today = new Date().toISOString().split("T")[0];
        const response = await fetch(
          `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/events?fromDate=${today}`
        );
        const result = await response.json();
        if (result.success && result.data) {
          // 過濾只顯示主要事件且有圖片的事件
          const mainEventsWithImages = result.data;
          console.log("Main events with images:", mainEventsWithImages.length);
          setEvents(mainEventsWithImages);
        } else {
          console.error("API response error:", result);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // 檢查是否需要顯示箭頭
  useEffect(() => {
    setShowArrows(events.length > 3);
  }, [events]);

  // 自動滾動事件
  useEffect(() => {
    if (events.length === 0) return;

    const interval = setInterval(() => {
      setCurrentEventIndex((prevIndex) => {
        // 當到達最後幾個事件時，重新開始
        if (prevIndex >= events.length - 3) {
          return 0;
        }
        return prevIndex + 1;
      });
    }, 2000); // 每2秒切換一次

    return () => clearInterval(interval);
  }, [events]);

  // 手動翻頁函數
  const handlePrevClick = () => {
    setCurrentEventIndex((prevIndex) => {
      if (prevIndex <= 0) {
        return Math.max(0, events.length - 5);
      }
      return prevIndex - 1;
    });
  };

  const handleNextClick = () => {
    setCurrentEventIndex((prevIndex) => {
      if (prevIndex >= events.length - 3) {
        return 0;
      }
      return prevIndex + 1;
    });
  };

  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate((prev) => prev + 1);
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // 等待翻譯資源載入
  useEffect(() => {
    if (i18n.isInitialized && i18n.store.data) {
      setIsReady(true);
    }
  }, [i18n.isInitialized, i18n.store.data]);

  // 如果還沒準備好，顯示載入狀態
  if (!isReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div className="mahjong-home" key={`${i18n.language}-${forceUpdate}`}>
      <header
        className="banner"
        style={{
          background: `url(${MainBackground}) center center / cover no-repeat`,
        }}
      >
        <div className="banner-content">
          <h1 className="banner-title">
            {getTranslation(i18n, "homePage.bannerTitle")}
          </h1>
          <p className="banner-subtitle">
            {getTranslation(i18n, "homePage.bannerSubtitle")}
          </p>
          <div>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={onContactClick}
              className="contact-us-button contact-us-button-large"
            >
              {getTranslation(i18n, "homePage.contactUsButton")}
            </Button>
          </div>
        </div>
      </header>

      <section id="upcoming-events" className="locations">
        <h2>{getTranslation(i18n, "appDescriptionPage.locations.title")}</h2>
        <div className="location-container">
          {showArrows && (
            <button
              className="arrow-button arrow-left"
              onClick={handlePrevClick}
              aria-label="Previous events"
            >
              ‹
            </button>
          )}
          <div className="location-list">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading events...</p>
              </div>
            ) : events.length > 0 ? (
              <>
                {events
                  .slice(currentEventIndex, currentEventIndex + 3)
                  .map((event, index) => (
                    <div
                      key={event.eventId}
                      className="location-card"
                      style={{
                        background: `url(${
                          event.imageUrl || EventFallbackImg
                        })`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <div className="event-info">
                        <div className="event-date">
                          {event.eventDate &&
                            new Date(event.eventDate).toLocaleDateString()}
                        </div>
                        <div className="event-title">
                          {i18n.language === "zh-cn" && event.titleSC}
                          {i18n.language === "zh-hk" && event.titleTC}
                          {i18n.language === "en-gb" && event.titleEN}
                          {!i18n.language.includes("zh") && event.titleEN}
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            ) : null}
          </div>
          {showArrows && (
            <button
              className="arrow-button arrow-right"
              onClick={handleNextClick}
              aria-label="Next events"
            >
              ›
            </button>
          )}
        </div>
      </section>

      <section id="photo" className="photo-section">
        <div className="photo-container">
          <h2 className="photo-title">
            {getTranslation(i18n, "photoPage.pageTitle")}
          </h2>
          <div className="photo-image">
            <div className="photo-image-row">
              <img
                src={PhotoPagePicture1}
                alt="Photos"
                className="photo-img"
                onClick={() => navigate("/photo")}
                style={{ cursor: "pointer" }}
              />
              <img
                src={PhotoPagePicture2}
                alt="Photos"
                className="photo-img"
                onClick={() => navigate("/photo")}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="photo-image-row">
              <img
                src={PhotoPagePicture3}
                alt="Photos"
                className="photo-img"
                onClick={() => navigate("/photo")}
                style={{ cursor: "pointer" }}
              />
              <img
                src={PhotoPagePicture1}
                alt="Photos"
                className="photo-img"
                onClick={() => navigate("/photo")}
                style={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
